import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PDFService } from '../pdf-service/pdf.service';
import { NotificationService } from '../notification-service/notification.service';
import {
	TextArticle,
	ImageArticle,
	ArticleType,
} from '@meta-press/shared-plugins';

@Component({
	selector: 'app-pdf-preview-modal',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './pdf-preview-modal.component.html',
	styleUrls: ['./pdf-preview-modal.component.scss'],
})
export class PDFPreviewModalComponent implements OnInit {
	// Services
	activeModal = inject(NgbActiveModal);
	pdfService = inject(PDFService);
	notificationService = inject(NotificationService);

	// Inputs
	@Input() pdfBlob?: Blob;
	@Input() filename!: string;
	@Input() title?: string;
	@Input() previewMode: 'pdf' | 'content' = 'pdf';
	@Input() contentPreview?: string;
	@Input() htmlElement?: HTMLElement; // For html2canvas generation
	@Input() sourceUrl?: string; // Source URL for domain extraction

	// Component state
	isLoading = true;
	loadingError?: string;
	isApproving = false; // Loading state for approval process

	// Extracted content
	textArticles: TextArticle[] = [];
	imageArticles: ImageArticle[] = [];

	// Display properties
	totalPages = 0;
	fileSize = '';

	// PDF Generation modes
	pdfGenerationMode: 'text-only' | 'text-and-images' | 'html2canvas' =
		'text-only';
	estimatedFileSize = '';
	isCalculatingSize = false;

	// PDF naming
	customFilename = '';
	showFilenameInput = false;

	// URL domain handling
	domainSuffix = '';

	ngOnInit(): void {
		// Initialize custom filename - remove any common file extensions
		this.customFilename = this.removeFileExtension(this.filename);

		// Extract domain suffix from source URL if available
		if (this.sourceUrl) {
			this.domainSuffix = this.extractDomainFromUrl(this.sourceUrl);
		}

		if (this.previewMode === 'pdf') {
			this.extractPDFContent();
			this.calculateFileSize();
		} else {
			this.setupContentPreview();
			this.calculateEstimatedFileSize();
		}
	}

	// Calculate and format file size
	private calculateFileSize(): void {
		if (this.pdfBlob) {
			this.fileSize = this.formatFileSize(this.pdfBlob.size);
		}
	}

	// Format file size for display
	private formatFileSize(bytes: number): string {
		if (bytes === 0) {
			return '0 B';
		}

		const units = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
	}

	// Setup content preview for HTML content (no PDF extraction needed)
	private setupContentPreview(): void {
		this.isLoading = false;
		this.loadingError = undefined;

		if (this.contentPreview) {
			// Create a single text article from the content
			const textArticle = new TextArticle(
				this.title || this.filename,
				'Content Preview',
				Date.now(),
				this.contentPreview,
				-1,
				[], // relatedArticles
				[], // tags
			);
			this.textArticles = [textArticle];
		}

		// No images in content preview
		this.imageArticles = [];
		this.totalPages = 1;
		this.fileSize = this.formatFileSize(this.contentPreview?.length || 0);
	}

	// Extract content from PDF for preview
	private async extractPDFContent(): Promise<void> {
		if (!this.pdfBlob) {
			this.loadingError = 'PDF blob not provided';
			this.isLoading = false;
			return;
		}

		try {
			this.isLoading = true;
			this.loadingError = undefined;

			// Convert blob to file for PDF service
			const file = new File([this.pdfBlob], this.filename, {
				type: 'application/pdf',
			});

			// Extract content using PDF service (text-only by default)
			const extractedContent =
				await this.pdfService.extractContentFromPDF(
					file,
					'PDF Preview',
					false, // includeImages = false (text-only)
				);

			this.textArticles = extractedContent.textArticles;
			this.imageArticles = extractedContent.imageArticles;

			// Calculate total pages from the content
			this.totalPages = Math.max(
				this.textArticles.length,
				this.imageArticles.length,
			);

			// If no content was extracted, show a message
			if (
				this.textArticles.length === 0 &&
				this.imageArticles.length === 0
			) {
				console.warn('No content could be extracted from PDF');
			}
		} catch (error) {
			console.error('Error extracting PDF content:', error);
			this.loadingError =
				'PDF-sisällön lukeminen epäonnistui: ' +
				(error as Error).message;
		} finally {
			this.isLoading = false;
		}
	}

	// Get combined text content for preview
	getCombinedTextContent(): string {
		if (this.textArticles.length === 0) {
			return 'Tekstisisältöä ei voitu lukea PDF-tiedostosta.';
		}

		return this.textArticles.map((article) => article.content).join('\n\n');
		// .substring(0, 2000) + (this.getTotalTextLength() > 2000 ? '...' : '');
	}

	// Get total text length
	private getTotalTextLength(): number {
		return this.textArticles
			.map((article) => article.content || '')
			.join('').length;
	}

	// Check if there are images to display
	hasImages(): boolean {
		return this.imageArticles.length > 0;
	}

	// Get first few images for preview
	getPreviewImages(): ImageArticle[] {
		return this.imageArticles.slice(0, 3); // Show max 3 images
	}

	// Calculate estimated file size based on generation mode
	async calculateEstimatedFileSize(): Promise<void> {
		if (this.isCalculatingSize) return;

		this.isCalculatingSize = true;

		try {
			let estimatedSize = 0;

			switch (this.pdfGenerationMode) {
				case 'text-only':
					// Estimate based on text content length (very rough estimate)
					const textLength = this.contentPreview?.length || 0;
					estimatedSize = Math.max(1000, textLength * 0.1); // Min 1KB, ~0.1 bytes per character
					break;

				case 'text-and-images':
					// Text size + estimated image compression
					const textSize = Math.max(
						1000,
						(this.contentPreview?.length || 0) * 0.1,
					);
					const imageCount =
						this.htmlElement?.querySelectorAll('img').length || 0;
					const estimatedImageSize = imageCount * 15000; // ~15KB per compressed image
					estimatedSize = textSize + estimatedImageSize;
					break;

				case 'html2canvas':
					// Much larger due to canvas rendering
					const elementSize = this.htmlElement
						? this.htmlElement.offsetWidth *
							this.htmlElement.offsetHeight *
							0.02
						: 50000;
					estimatedSize = Math.max(50000, elementSize); // Min 50KB for canvas
					break;
			}

			this.estimatedFileSize = this.formatFileSize(estimatedSize);
		} catch (error) {
			console.error('Error calculating estimated file size:', error);
			this.estimatedFileSize = 'Tuntematon';
		} finally {
			this.isCalculatingSize = false;
		}
	}

	// Handle PDF generation mode change
	onGenerationModeChange(): void {
		this.calculateEstimatedFileSize();
	}

	// Check if mode is available based on available data
	isModeAvailable(
		mode: 'text-only' | 'text-and-images' | 'html2canvas',
	): boolean {
		// All modes are always available now
		return true;
	}

	// Remove common file extensions from filename
	private removeFileExtension(filename: string): string {
		// Common file extensions to remove
		const extensions = [
			'.pdf',
			'.PDF',
			'.txt',
			'.doc',
			'.docx',
			'.html',
			'.htm',
			'.xml',
		];
		let cleanName = filename;

		for (const ext of extensions) {
			if (cleanName.endsWith(ext)) {
				cleanName = cleanName.slice(0, -ext.length);
				break;
			}
		}

		return cleanName;
	}

	// Validate custom filename - now more permissive
	private isValidFilename(filename: string): boolean {
		if (!filename || filename.trim().length === 0) return false;
		// Only restrict truly problematic characters for filesystem
		// Allow : ; ( ) [ ] and Scandic characters (åäöÅÄÖ etc.)
		const invalidChars = /[<>"/\\|?*]/;
		return !invalidChars.test(filename);
	}

	// Extract domain from URL for display
	private extractDomainFromUrl(url: string): string {
		try {
			const urlObj = new URL(url);
			let hostname = urlObj.hostname;

			// Remove www. if present for cleaner display, but keep it if it's the only part
			if (hostname.startsWith('www.') && hostname.length > 4) {
				return hostname;
			} else if (
				!hostname.startsWith('www.') &&
				!hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
			) {
				// Add www. for domains that don't have it and aren't IP addresses
				return `www.${hostname}`;
			}

			return hostname;
		} catch (error) {
			// If URL parsing fails, try to extract domain manually
			const match = url.match(/https?:\/\/([^\/]+)/);
			return match ? match[1] : '';
		}
	}

	// Get the final PDF filename
	private getFinalFilename(): string {
		const name = this.customFilename?.trim() || this.filename;
		const baseName = this.isValidFilename(name) ? name : 'document';

		// Add domain suffix if available
		if (this.domainSuffix) {
			return `${baseName} (${this.domainSuffix})`;
		}

		return baseName;
	}

	// Generate text-only PDF using the PDF service
	private async generateTextOnlyPDF(): Promise<Blob> {
		if (!this.contentPreview) {
			throw new Error(
				'No content available for text-only PDF generation',
			);
		}

		const title = this.title || this.getFinalFilename();
		const filename = this.getFinalFilename();

		return await this.pdfService.generateTextOnlyPDF(
			this.contentPreview,
			title,
			filename,
			undefined, // sourceUrl
			false, // includeImages = false
			undefined, // htmlElement not needed for text-only
		);
	}

	// Generate text-and-images PDF using the PDF service
	private async generateTextAndImagesPDF(): Promise<Blob> {
		if (!this.contentPreview) {
			throw new Error(
				'No content available for text-and-images PDF generation',
			);
		}

		const title = this.title || this.getFinalFilename();
		const filename = this.getFinalFilename();

		return await this.pdfService.generateTextOnlyPDF(
			this.contentPreview,
			title,
			filename,
			undefined, // sourceUrl
			true, // includeImages = true
			this.htmlElement, // htmlElement for image extraction
		);
	}

	// Generate PDF with html2canvas and compression
	private async generateHtml2CanvasPDF(): Promise<Blob> {
		if (!this.htmlElement) {
			throw new Error('HTML element required for html2canvas generation');
		}

		const html2canvas = await import('html2canvas');
		const canvas = await html2canvas.default(this.htmlElement, {
			scale: 1, // Reduced scale for better compression
			useCORS: true,
			logging: false,
			backgroundColor: '#ffffff',
			removeContainer: true,
		});

		// Convert canvas to compressed image
		const imgData = canvas.toDataURL('image/jpeg', 0.8); // Good quality for readability

		// Calculate optimal PDF dimensions based on canvas aspect ratio
		const margin = 15; // 15mm margin on all sides
		const maxWidth = 210; // A4 width in mm
		const minWidth = 100; // Minimum reasonable width

		// Calculate content dimensions with good resolution
		// Aim for roughly 150 DPI equivalent (canvas pixels to mm conversion)
		const mmPerPixel = 0.2; // Approximately 127 DPI (25.4mm per inch / 200 pixels per inch)

		let contentWidth = Math.max(
			minWidth,
			Math.min(maxWidth, canvas.width * mmPerPixel),
		);
		let contentHeight = canvas.height * mmPerPixel;

		// If content is too narrow, scale up proportionally
		if (contentWidth < maxWidth * 0.6) {
			// If less than 60% of A4 width
			const scale = (maxWidth * 0.7) / contentWidth; // Scale to 70% of A4 width
			contentWidth *= scale;
			contentHeight *= scale;
		}

		// Calculate final PDF page dimensions
		const pdfWidth = contentWidth + 2 * margin;
		const pdfHeight = contentHeight + 2 * margin;

		// Create PDF with custom dimensions
		const pdf = new (await import('jspdf')).jsPDF({
			orientation: pdfWidth < pdfHeight ? 'p' : 'l',
			unit: 'mm',
			format: [pdfWidth, pdfHeight],
			compress: true,
		});

		// Position content with margins
		const x = margin;
		const y = margin;

		pdf.addImage(
			imgData,
			'JPEG',
			x,
			y,
			contentWidth,
			contentHeight,
			undefined,
			'FAST',
		);

		return pdf.output('blob');
	}

	// Download the PDF file
	async downloadPDF(): Promise<void> {
		try {
			let pdfBlob: Blob;

			// If we have a pdfBlob already, use it (PDF preview mode)
			if (this.pdfBlob) {
				pdfBlob = this.pdfBlob;
			}
			// If we're in content preview mode, use selected generation mode
			else if (this.previewMode === 'content') {
				this.notificationService.ShowToast(
					'Odotetaan materiaalin tarkistusta...',
					'primary',
				);

				// Generate PDF based on selected generation mode
				switch (this.pdfGenerationMode) {
					case 'text-only':
						pdfBlob = await this.generateTextOnlyPDF();
						break;
					case 'text-and-images':
						pdfBlob = await this.generateTextAndImagesPDF();
						break;
					case 'html2canvas':
					default:
						if (!this.htmlElement) {
							throw new Error(
								'HTML element required for HTML2Canvas PDF generation',
							);
						}
						pdfBlob = await this.generateHtml2CanvasPDF();
						break;
				}
			}
			// No content available for download
			else {
				this.notificationService.ShowToast(
					'Materiaalin muuntaminen tiedostoksi epäonnistui.',
					'danger',
				);
				return;
			}

			// Use the PDFService to save the PDF with custom filename
			const finalFilename =
				this.previewMode === 'content'
					? this.getFinalFilename() + '.pdf'
					: this.filename;
			this.pdfService.savePDF(pdfBlob, finalFilename);
			this.notificationService.ShowToast(
				'Materiaali ladattu onnistuneesti!',
				'success',
			);
		} catch (error) {
			console.error('Error downloading PDF:', error);
			this.notificationService.ShowToast(
				'Materiaalin lataus epäonnistui.',
				'danger',
			);
		}
	}

	// Approve the PDF and close modal with success
	async approve(): Promise<void> {
		if (this.isApproving) return; // Prevent multiple clicks

		try {
			this.isApproving = true;

			// Generate PDF based on current mode (html2canvas by default)
			let pdfBlob: Blob;

			if (this.previewMode === 'content') {
				// Generate PDF based on selected generation mode
				switch (this.pdfGenerationMode) {
					case 'text-only':
						pdfBlob = await this.generateTextOnlyPDF();
						break;
					case 'text-and-images':
						pdfBlob = await this.generateTextAndImagesPDF();
						break;
					case 'html2canvas':
					default:
						pdfBlob = await this.generateHtml2CanvasPDF();
						break;
				}
			} else {
				// For PDF preview mode, use the existing PDF blob
				if (this.pdfBlob) {
					pdfBlob = this.pdfBlob;
				} else {
					throw new Error('No PDF available for preview mode');
				}
			}

			this.activeModal.close({
				approved: true,
				generationMode: this.pdfGenerationMode,
				htmlElement: this.htmlElement,
				customFilename: this.getFinalFilename(),
				pdfBlob: pdfBlob,
			});
		} catch (error) {
			console.error('Error generating PDF for approval:', error);
			this.notificationService.ShowToast(
				'PDF-luonti epäonnistui',
				'danger',
			);
			this.isApproving = false; // Re-enable buttons on error
		}
	}

	// Cancel the PDF upload
	cancel(): void {
		this.activeModal.close({ approved: false });
	}

	// Dismiss modal (same as cancel)
	dismiss(): void {
		this.activeModal.dismiss({ approved: false });
	}
}
